import { useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { invitationStore, inviteTokenSelector } from '@stores/invitation.store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { removeEmptyStrings } from '@utils/form-validator.util.ts';
import { useStore } from 'zustand';
import { InviteCreateDto, InviteRoleUpdateDto, InviteSelectDto } from '../types/api.types.ts';

export const useSubmitInvites = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onError = (error: Error) => {
    console.log('Error submitting invites:', error);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.userCompanies.getCompanyUsers,
    });
  };

  return useMutation<InviteSelectDto[], Error, InviteCreateDto[]>({
    mutationFn: async (invites) => {
      return await apiPost<InviteSelectDto[]>({
        path: '/v1/invites/bulk',
        body: { invites },
      });
    },
    onError,
    onSuccess,
  });
};

export const useAcceptInvite = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();
  const inviteToken = useStore(invitationStore, inviteTokenSelector);

  const onError = (error: Error) => {
    console.log('Error accepting invite:', error);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
  };

  return useMutation<InviteSelectDto, Error, string>({
    mutationFn: async (inviteId) => {
      return await apiPatch<InviteSelectDto>({
        body: { inviteToken },
        path: `/v1/invites/${inviteId}/accept`,
      });
    },
    onError,
    onSuccess,
  });
};

export const useRejectInvite = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();
  const inviteToken = useStore(invitationStore, inviteTokenSelector);

  const onError = (error: Error) => {
    console.log('Error rejecting invite:', error);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
  };

  return useMutation<InviteSelectDto, Error, string>({
    mutationFn: async (inviteId) => {
      return await apiPatch<InviteSelectDto>({
        body: { inviteToken },
        path: `/v1/invites/${inviteId}/reject`,
      });
    },
    onError,
    onSuccess,
  });
};

export const useDeleteInvite = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (inviteId: string) => {
      await apiDelete({ path: `/v1/invites/${inviteId}` }); // Ignore response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
    },
  });
};

export const useUpdateInvite = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<InviteSelectDto, Error, { id: string; data: InviteRoleUpdateDto }>({
    mutationFn: async ({ id, data }) => {
      const cleanedData = removeEmptyStrings(data);
      return await apiPatch<InviteSelectDto>({ path: `/v1/invites/${id}`, body: cleanedData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
    },
  });
};
